import { Routes, SpecificModules } from '@/assets/configs'
import { UrlParamType } from '@/modules/utils'
import { useEMTDispatch, useEMTState } from '@/store'
import { appendParamsIfNeeded } from '@/utils'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import useFirstRouterQueryParam from './useFirstRouterQueryParam'
import { FavoritesServiceHelper } from '@/modules/Favorites'
import { FavoriteType, setFavoriteType } from '@/modules/Favorites/apiTypes/GetFavorites'
import { RegistrationType } from '@/dataV3/User/ConnectedUser'
import useCurrentUser from '@/hooks/useCurrentUser'
import usePetSitterConnectionRoutes from '@/hooks/usePetSitterConnectionRoutes'
import { Constants } from '@/assets'
import { UserServiceHelper } from '@/modules/User'
import { usePetsittingMode } from '@/store/usePetsittingMode'

export const enum DogOwnerAvailabilityStepsList {
  Profile = 'profile',
  Needs = 'needs',
  Availability = 'availability',
}

const useConnectionRoutes = () => {
  const router = useRouter()
  const dispatch = useEMTDispatch()
  const [firstFavoriteCall, setFirstFavoriteCall] = useState<boolean>(true)
  const { isPetsittingMode } = usePetsittingMode()

  const [queryType, queryNextPage] = useFirstRouterQueryParam(['type', 'nextPage'])
  const { slugRegistration } = usePetSitterConnectionRoutes({ shouldInitialize: false })

  const { favorites } = useEMTState()
  const { currentUnknownUser } = useCurrentUser()

  useEffect(() => {
    if (currentUnknownUser && firstFavoriteCall && currentUnknownUser.registrationType === RegistrationType.borrower) {
      setFirstFavoriteCall(() => false)
      FavoritesServiceHelper.dispatchGetFavoritesAsync(dispatch)({
        searchParams: { type: setFavoriteType(FavoriteType.sent) },
      })
    }
  }, [firstFavoriteCall])

  const getRouteWithCurrentNextPage = (route: string) => {
    return appendParamsIfNeeded(route, { nextPage: queryNextPage, type: queryType })
  }

  const safeReplace = (url: string) => {
    if (url === router.asPath) return // déjà sur la bonne URL, ne rien faire
    router.replace(url)
  }

  const checkConnectionRoute = (pathname = router.pathname) => {
    switch (pathname) {
      case Routes.Connection.CreateAccount.AddressPhoto:
      case Routes.Connection.CreateAccount.Description:
      case Routes.Connection.CreateAccount.Availability:
      case Routes.Connection.CreateAccount.Dogs:
      case Routes.Connection.CreateAccount.Favorites:
      case Routes.Connection.CreateAccount.Partners:
      case Routes.Connection.CreateAccount.AddPet:
      case Routes.Connection.CreateAccount.SignUpCallRequest:
        return true
      default:
        return false
    }
  }

  const finalizeInscription = async (santevet = false, redirect = true) => {
    try {
      await UserServiceHelper.dispatchCompleteRegisterAsync(dispatch)({ santevet })
      if (redirect) {
        if (queryType === UrlParamType.Owner && isPetsittingMode) {
          router.push({
            pathname: Routes.Home,
            query: { registration: true, type: queryType },
          })
        } else {
          router.push(Routes.Home)
        }
      }
    } catch (err) {
      console.error('Failed to finalize inscription:', err)
    }
  }

  const getNextRoute = (user = currentUnknownUser, options?: { nextPage?: string; fromRegister?: boolean }): string => {
    const queryFromRegister = router.pathname.includes('create-your-profile')

    const queryParams: {
      type: UrlParamType | undefined
      nextPage: string | undefined
      fromRegister: boolean
    } = {
      type: undefined,
      nextPage: options?.nextPage || queryNextPage,
      fromRegister: options?.fromRegister !== undefined ? options.fromRegister : queryFromRegister,
    }

    if (!user?.id) {
      return appendParamsIfNeeded(Routes.Connection.SignIn, queryParams)
    }

    if (['PendingAcquisitionPetsitter'].includes(user?.['@type'])) {
      const stepConfig = {
        1: Routes.Connection.SignUp.Details,
        2: `${Routes.Pro.SignUp.CompleteProfile}?step=profile-1`,
        3: `${Routes.Pro.SignUp.CompleteProfile}?step=profile-2`,
        4: `${Routes.Pro.SignUp.Experience}?step=experience-1`,
        5: `${Routes.Pro.SignUp.Experience}?step=experience-2`,
        6: `${Routes.Pro.SignUp.Services}?step=services-1`,
        7: `${Routes.Pro.SignUp.Services}?step=services-2`,
        8: `${Routes.Pro.SignUp.Services}?step=services-3`,
        9: `${Routes.Pro.SignUp.Services}?step=services-4`,
        10: `${Routes.Pro.SignUp.Psychology}?step=psychology-1`,
        11: `${Routes.Pro.SignUp.Knowledge}?step=knowledge-1`,
        12: `${Routes.Pro.SignUp.Identity}?step=identity-1`,
        13: Routes.Pro.Dashboard,
      }
      return appendParamsIfNeeded(stepConfig[slugRegistration] || stepConfig[1])
    }

    if (['ConnectedPetsitter'].includes(user?.['@type'])) {
      return appendParamsIfNeeded(Routes.Pro.Dashboard)
    }

    if (['ConnectedUser'].includes(user?.['@type'])) {
      if (
        router.pathname === Routes.Connection.CreateAccount.SignUpCallRequest ||
        (isPetsittingMode && queryFromRegister)
      ) {
        return appendParamsIfNeeded(Routes.PetSitter.Search)
      }
      return appendParamsIfNeeded(Routes.Home)
    }

    let isOwner = false

    switch (user.registrationType) {
      case RegistrationType.petowner:
        queryParams.type = UrlParamType.Owner
        isOwner = true
        break
      case RegistrationType.borrower:
        queryParams.type = UrlParamType.Borrower
        break
    }

    if (user?.['@type'] === 'PendingAcquisitionUser') {
      if (router.pathname === Routes.Connection.CreateAccount.SignUpCallRequest)
        return appendParamsIfNeeded(Routes.Connection.CreateAccount.SignUpCallRequest, queryParams)
      if (user.registrationType === RegistrationType.borrower) {
        // Register Favorites step
        if (
          options?.nextPage?.includes(Routes.Connection.CreateAccount.Favorites) &&
          !favorites?.[FavoriteType.sent].totalCount &&
          SpecificModules.register.favorites &&
          !isOwner
        ) {
          return appendParamsIfNeeded(Routes.Connection.CreateAccount.Favorites, queryParams)
        }
        // Register step 3 (common)
        if (
          !user.acquisitionStatus.haveMainImage ||
          !user.acquisitionStatus.haveAddress ||
          !user.acquisitionStatus.havePhone
        ) {
          return appendParamsIfNeeded(Routes.Connection.CreateAccount.AddressPhoto, queryParams)
        }

        // Register step 4 (common)
        if (!user.acquisitionStatus.haveDescription) {
          return appendParamsIfNeeded(Routes.Connection.CreateAccount.Description, queryParams)
        }

        // Register step 5 (borrower)
        if (!user.acquisitionStatus.haveBorrowerMemberRequiredOptions) {
          return appendParamsIfNeeded(Routes.Connection.CreateAccount.Availability, queryParams)
        }

        // Register step 7 (common)
        if (
          SpecificModules.register.insurances &&
          Constants.brand === 'emt' &&
          queryParams.fromRegister &&
          !user.acquisitionStatus.haveInsurance
        ) {
          return appendParamsIfNeeded(Routes.Connection.CreateAccount.Partners, queryParams)
        } else if (
          queryParams.nextPage === Routes.Connection.CreateAccount.Partners &&
          (!SpecificModules.register.insurances || Constants.brand !== 'emt')
        ) {
          return appendParamsIfNeeded(Routes.Home, { type: queryParams.type })
        }

        // If nothing is missing then complete call and go home
        if (
          user.acquisitionStatus.haveMainImage &&
          user.acquisitionStatus.haveAddress &&
          user.acquisitionStatus.havePhone &&
          user.acquisitionStatus.haveDescription &&
          user.acquisitionStatus.haveBorrowerMemberRequiredOptions &&
          // (!SpecificModules.register.insurances || user.acquisitionStatus.haveInsurance || Constants.brand !== 'emt') &&
          !checkConnectionRoute()
        ) {
          finalizeInscription()
        }
      }

      if (user.registrationType === RegistrationType.petowner) {
        // Register step 5.1 (owner)
        if (
          !user.animals?.length ||
          user.animals.some((animal) => !animal.acquisitionStatus?.haveAnimalRequiredOptions)
        ) {
          return appendParamsIfNeeded(Routes.Connection.CreateAccount.AddPet, {
            ...queryParams,
          })
        }

        // Register step 5.2 (owner)
        if (!user.acquisitionStatus.haveInsurance && Constants.brand === 'emt' && SpecificModules.register.insurances) {
          return appendParamsIfNeeded(Routes.Connection.CreateAccount.Partners, {
            ...queryParams,
          })
        }

        // If nothing is missing then complete call and go home
        if (
          user.acquisitionStatus.haveMainImage &&
          user.acquisitionStatus.haveAddress &&
          user.acquisitionStatus.havePhone &&
          user.acquisitionStatus.haveDescription &&
          user.acquisitionStatus.havePetownerMemberRequiredOptions &&
          // (!SpecificModules.register.insurances || user.acquisitionStatus.haveInsurance || Constants.brand !== 'emt') &&
          user.animals?.length &&
          !checkConnectionRoute()
        ) {
          finalizeInscription()
        }
      }
    }

    // It means the current user is a ConnectedUser, so goto Home (or nextPage)
    return appendParamsIfNeeded(queryParams.nextPage || Routes.Home, { type: queryParams.type })
  }

  const navigateToNextRoute = (user = currentUnknownUser, options?: { nextPage?: string; fromRegister?: boolean }) => {
    const nextRoute = getNextRoute(user, options)

    safeReplace(nextRoute)
  }

  return {
    getNextRoute,
    navigateToNextRoute,
    checkConnectionRoute,
    getRouteWithCurrentNextPage,
    finalizeInscription,
  } as const
}

export default useConnectionRoutes
